import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';

const PriveShop = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [pastOrders, setPastOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products'); // 'products', 'cart', or 'orders'
  const [cart, setCart] = useState([]);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [variationDetails, setVariationDetails] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null); // For order details modal
  
  // Filter and sort states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  // Prive Bakery WordPress site credentials
  const WP_SITE = 'https://moremoneymoreproblems.co.za';
  const CONSUMER_KEY = 'ck_97c4fab5aea41adae69ea144dd70bcb9227e41cb';
  const CONSUMER_SECRET = 'cs_a6145f8f156cdec8eca64308da48006cfca49d35';

  useEffect(() => {
    fetchProducts();
    fetchPastOrders();
    loadCart();
  }, []);

  const loadCart = () => {
    const savedCart = JSON.parse(localStorage.getItem('priveCart') || '[]');
    setCart(savedCart);
  };

  const saveCart = (newCart) => {
    localStorage.setItem('priveCart', JSON.stringify(newCart));
    setCart(newCart);
  };

  const fetchProducts = async () => {
    try {
      const auth = btoa(`${CONSUMER_KEY}:${CONSUMER_SECRET}`);
      
      // Fetch all products from Prive Bakery
      const response = await fetch(`${WP_SITE}/wp-json/wc/v3/products?per_page=50&status=publish`, {
        headers: { 
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setProducts(data);
      setLoading(false);
      console.log('Products loaded:', data.length);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
      alert('Failed to load products. Please check console for details.');
    }
  };

  // Fetch variations only when user interacts with the dropdown
  const fetchProductVariations = async (productId, variationIds) => {
    // Check if already fetched
    if (variationDetails[productId]) {
      return;
    }

    try {
      const auth = btoa(`${CONSUMER_KEY}:${CONSUMER_SECRET}`);
      const variationPromises = variationIds.slice(0, 10).map(varId => // Limit to 10 variations
        fetch(`${WP_SITE}/wp-json/wc/v3/products/${productId}/variations/${varId}`, {
          headers: { 
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        })
        .then(res => res.ok ? res.json() : null)
        .catch(err => {
          console.error(`Error fetching variation ${varId}:`, err);
          return null;
        })
      );

      const variations = await Promise.all(variationPromises);
      const validVariations = variations.filter(v => v !== null);

      if (validVariations.length > 0) {
        setVariationDetails(prev => ({
          ...prev,
          [productId]: validVariations
        }));
      }
    } catch (error) {
      console.error('Error fetching variations:', error);
    }
  };

  const getVariationName = (variation) => {
    if (!variation.attributes || variation.attributes.length === 0) {
      return variation.name || `Option ${variation.id}`;
    }
    
    return variation.attributes
      .map(attr => attr.option)
      .join(', ');
  };

  const fetchPastOrders = async () => {
    try {
      const auth = btoa(`${CONSUMER_KEY}:${CONSUMER_SECRET}`);
      
      // Get user email from AuthContext or profile
      const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      const userEmail = user?.email || profile.email || 'test@example.com';
      
      console.log('Fetching orders for email:', userEmail);
      
      // Fetch all recent orders and filter by email
      // WooCommerce search parameter searches across multiple fields including billing email
      const response = await fetch(
        `${WP_SITE}/wp-json/wc/v3/orders?per_page=100&orderby=date&order=desc`,
        {
          headers: { 
            'Authorization': `Basic ${auth}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const allOrders = await response.json();
        
        // Filter orders by billing email (case-insensitive)
        const userOrders = allOrders.filter(order => 
          order.billing?.email?.toLowerCase() === userEmail.toLowerCase()
        );
        
        setPastOrders(userOrders);
        console.log('Orders loaded:', userOrders.length, 'out of', allOrders.length, 'total orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleAddToCart = (product) => {
    const selectedVariation = selectedVariations[product.id];
    
    // Check if product has variations and none is selected
    if (product.type === 'variable' && product.variations && product.variations.length > 0 && !selectedVariation) {
      alert('Please select a variation before adding to cart');
      return;
    }

    // Get variation details if applicable
    let variationName = '';
    let variationPrice = product.price;
    
    if (selectedVariation && variationDetails[product.id]) {
      const variation = variationDetails[product.id].find(v => v.id === selectedVariation);
      if (variation) {
        variationName = getVariationName(variation);
        variationPrice = variation.price;
      }
    }

    const cartItem = {
      id: product.id,
      name: product.name,
      price: variationPrice,
      image: product.images[0]?.src,
      variation_id: selectedVariation || 0,
      variation_name: variationName,
      quantity: 1
    };

    // Check if item already in cart
    const existingItemIndex = cart.findIndex(
      item => item.id === product.id && item.variation_id === (selectedVariation || 0)
    );

    let newCart;
    if (existingItemIndex >= 0) {
      newCart = [...cart];
      newCart[existingItemIndex].quantity += 1;
    } else {
      newCart = [...cart, cartItem];
    }

    saveCart(newCart);
    alert(`${product.name}${variationName ? ` - ${variationName}` : ''} added to cart!`);
  };

  const handleRemoveFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    saveCart(newCart);
  };

  const handleUpdateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    const newCart = [...cart];
    newCart[index].quantity = newQuantity;
    saveCart(newCart);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (parseFloat(item.price) * item.quantity), 0).toFixed(2);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      alert('Your cart is empty!');
      return;
    }

    const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
    
    // Get user email from AuthContext or profile
    const userEmail = user?.email || profile.email || 'test@example.com';
    const userName = user?.name || profile.firstName || 'Test';
    
    // Test data if profile is empty
    const orderData = {
      payment_method: 'cod', // Cash on delivery for testing
      payment_method_title: 'Test Order',
      set_paid: false,
      
      billing: {
        first_name: userName.split(' ')[0] || 'Test',
        last_name: userName.split(' ').slice(1).join(' ') || 'User',
        email: userEmail,
        phone: profile.phone || '+27123456789',
        address_1: profile.billingAddress || '123 Test Street',
        address_2: profile.billingAddress2 || '',
        city: profile.billingCity || 'Johannesburg',
        state: profile.billingState || 'Gauteng',
        postcode: profile.billingZip || '2000',
        country: profile.billingCountry || 'ZA'
      },
      
      shipping: {
        first_name: profile.firstName || 'Test',
        last_name: profile.lastName || 'User',
        address_1: profile.shippingAddress || profile.billingAddress || '123 Test Street',
        address_2: profile.shippingAddress2 || profile.billingAddress2 || '',
        city: profile.shippingCity || profile.billingCity || 'Johannesburg',
        state: profile.shippingState || profile.billingState || 'Gauteng',
        postcode: profile.shippingZip || profile.billingZip || '2000',
        country: profile.shippingCountry || profile.billingCountry || 'ZA'
      },
      
      line_items: cart.map(item => ({
        product_id: item.id,
        variation_id: item.variation_id,
        quantity: item.quantity
      })),
      
      meta_data: [
        {
          key: '_purchased_via_app',
          value: 'React Wealth Club - Prive Card'
        },
        {
          key: '_purchase_timestamp',
          value: new Date().toISOString()
        }
      ]
    };

    try {
      const auth = btoa(`${CONSUMER_KEY}:${CONSUMER_SECRET}`);
      
      const response = await fetch(`${WP_SITE}/wp-json/wc/v3/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${auth}`
        },
        body: JSON.stringify(orderData)
      });

      const order = await response.json();
      
      if (order.id) {
        console.log('Order created:', order);
        alert(`✅ Order #${order.number} created successfully!\n\nTotal: R${order.total}\n\nThis is a test order - no payment required.`);
        
        // Clear cart
        saveCart([]);
        
        // Refresh orders list
        fetchPastOrders();
        
        // Switch to orders tab to show the new order
        setActiveTab('orders');
      } else {
        console.error('Order creation failed:', order);
        alert('Order creation failed. Check console for details.');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Purchase failed. Please try again.');
    }
  };

  const getOrderStatusColor = (status) => {
    switch(status) {
      case 'completed': return '#4caf50';
      case 'processing': return '#ff9800';
      case 'pending': return '#2196f3';
      case 'cancelled': return '#f44336';
      case 'on-hold': return '#9e9e9e';
      default: return '#999';
    }
  };

  // Get unique categories from products
  const getCategories = () => {
    const categories = products.flatMap(p => p.categories || []);
    const uniqueCategories = [...new Map(categories.map(cat => [cat.id, cat])).values()];
    return uniqueCategories;
  };

  // Filter and sort products
  const getFilteredAndSortedProducts = () => {
    let filtered = [...products];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(term) ||
        (product.short_description && product.short_description.toLowerCase().includes(term)) ||
        (product.description && product.description.toLowerCase().includes(term))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => 
        product.categories && product.categories.some(cat => cat.id === parseInt(selectedCategory))
      );
    }

    // Sorting
    if (sortBy === 'price-asc') {
      filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    } else if (sortBy === 'price-desc') {
      filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    } else if (sortBy === 'name-asc') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'name-desc') {
      filtered.sort((a, b) => b.name.localeCompare(a.name));
    }

    return filtered;
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-wrapper">
          <h1 className="page-title">Loading Prive Bakery...</h1>
          <div style={{textAlign: 'center', padding: '50px'}}>
            <i className="fas fa-spinner fa-spin" style={{fontSize: '48px', color: '#c9a961'}}></i>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-wrapper">
        <h1 className="page-title">Shop our bakery</h1>
        <div className="underline"></div>
        <p className="page-subtitle">Exclusive Access to Prive Bakery</p>
        
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '0',
          marginBottom: '30px',
          borderBottom: '2px solid #eee',
          flexWrap: 'nowrap',
          overflow: 'auto'
        }}>
          <button
            onClick={() => setActiveTab('products')}
            style={{
              flex: 1,
              padding: '12px 8px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'products' ? '3px solid #c9a961' : 'none',
              color: activeTab === 'products' ? '#c9a961' : '#666',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              textAlign: 'center'
            }}
          >
            <i className="fas fa-shopping-bag"></i> Products ({products.length})
          </button>
          <button
            onClick={() => setActiveTab('cart')}
            style={{
              flex: 1,
              padding: '12px 8px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'cart' ? '3px solid #c9a961' : 'none',
              color: activeTab === 'cart' ? '#c9a961' : '#666',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              position: 'relative',
              whiteSpace: 'nowrap',
              textAlign: 'center'
            }}
          >
            <i className="fas fa-shopping-cart"></i> Cart 
            {cart.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                background: '#e74c3c',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {cart.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            style={{
              flex: 1,
              padding: '12px 8px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'orders' ? '3px solid #c9a961' : 'none',
              color: activeTab === 'orders' ? '#c9a961' : '#666',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              textAlign: 'center'
            }}
          >
            <i className="fas fa-history"></i> Orders ({pastOrders.length})
          </button>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            {/* Filters and Search */}
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '30px',
              marginTop: '20px'
            }}>
              {/* Search Bar */}
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#c9a961'}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              </div>

              {/* Filters Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '10px'
              }}>
                {/* Category Filter */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Categories</option>
                  {getCategories().map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>

                {/* Sort Filter */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="default">Sort By</option>
                  <option value="name-asc">Name: A-Z</option>
                  <option value="name-desc">Name: Z-A</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                </select>

                {/* Clear Filters */}
                {(searchTerm || selectedCategory !== 'all' || sortBy !== 'default') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                      setSortBy('default');
                    }}
                    style={{
                      padding: '10px',
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>

              {/* Results Count */}
              <div style={{
                marginTop: '15px',
                fontSize: '14px',
                color: '#666',
                fontWeight: '500'
              }}>
                Showing {getFilteredAndSortedProducts().length} of {products.length} products
              </div>
            </div>

            {products.length === 0 ? (
              <div style={{textAlign: 'center', padding: '50px', color: '#999'}}>
                <i className="fas fa-box-open" style={{fontSize: '64px', marginBottom: '20px'}}></i>
                <p>No products available at the moment.</p>
              </div>
            ) : getFilteredAndSortedProducts().length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: 'white',
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <i className="fas fa-search" style={{ fontSize: '48px', color: '#ddd', marginBottom: '20px' }}></i>
                <h3 style={{ color: '#666', marginBottom: '10px' }}>No products found</h3>
                <p style={{ color: '#999' }}>Try adjusting your filters or search term</p>
              </div>
            ) : (
              <div className="blog-grid">
                {getFilteredAndSortedProducts().map((product) => (
                  <div key={product.id} className="blog-card">
                    <img 
                      src={product.images[0]?.src || 'https://via.placeholder.com/400x300?text=Prive+Product'} 
                      alt={product.name} 
                      className="blog-image"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300?text=Prive+Product';
                      }}
                    />
                    <div className="blog-content">
                      <div className="blog-meta">
                        <span className="blog-tag">
                          {product.categories[0]?.name || 'Product'}
                        </span>
                        {product.stock_status === 'instock' ? (
                          <span style={{color: 'green'}}>
                            <i className="fas fa-check-circle"></i> In Stock
                          </span>
                        ) : (
                          <span style={{color: 'red'}}>
                            <i className="fas fa-times-circle"></i> Out of Stock
                          </span>
                        )}
                      </div>
                      
                      <h3 className="blog-title">{product.name}</h3>
                      
                      {product.short_description && (
                        <div 
                          dangerouslySetInnerHTML={{ __html: product.short_description }} 
                          className="blog-excerpt"
                          style={{
                            maxHeight: '100px',
                            overflow: 'hidden'
                          }}
                        />
                      )}
                      
                      {/* Product Variations */}
                      {product.type === 'variable' && product.variations && product.variations.length > 0 && (
                        <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                            Select Option:
                          </label>
                          <select
                            value={selectedVariations[product.id] || ''}
                            onChange={(e) => setSelectedVariations({
                              ...selectedVariations,
                              [product.id]: parseInt(e.target.value)
                            })}
                            onFocus={() => fetchProductVariations(product.id, product.variations)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '5px',
                              border: '1px solid #ddd',
                              fontSize: '14px'
                            }}
                          >
                            <option value="">Choose an option</option>
                            {variationDetails[product.id] ? (
                              variationDetails[product.id].map((variation) => (
                                <option key={variation.id} value={variation.id}>
                                  {getVariationName(variation)} - R{variation.price}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>Click to load options...</option>
                            )}
                          </select>
                        </div>
                      )}
                      
                      <div style={{
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginTop: '15px',
                        gap: '8px'
                      }}>
                        <div style={{fontSize: '16px', fontWeight: 'bold', color: '#c9a961'}}>
                          R{product.price}
                        </div>
                        
                        <button 
                          onClick={() => handleAddToCart(product)}
                          className="blog-read-more"
                          disabled={product.stock_status !== 'instock'}
                          style={{
                            border: 'none',
                            padding: '8px 12px',
                            cursor: product.stock_status === 'instock' ? 'pointer' : 'not-allowed',
                            opacity: product.stock_status === 'instock' ? 1 : 0.5,
                            fontSize: '11px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {product.stock_status === 'instock' ? (
                            <>Add <i className="fas fa-cart-plus"></i></>
                          ) : (
                            'Sold Out'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cart Tab */}
        {activeTab === 'cart' && (
          <div>
            {cart.length === 0 ? (
              <div style={{textAlign: 'center', padding: '50px', color: '#999'}}>
                <i className="fas fa-shopping-cart" style={{fontSize: '64px', marginBottom: '20px'}}></i>
                <p>Your cart is empty!</p>
                <button 
                  onClick={() => setActiveTab('products')}
                  className="blog-read-more"
                  style={{marginTop: '20px'}}
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <div>
                {cart.map((item, index) => (
                  <div key={index} style={{
                    background: 'white',
                    padding: '10px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    marginBottom: '10px',
                    display: 'flex',
                    gap: '10px'
                  }}>
                    <img 
                      src={item.image || 'https://via.placeholder.com/100x100?text=Product'} 
                      alt={item.name}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        flexShrink: 0
                      }}
                    />
                    <div style={{flex: 1, minWidth: 0, overflow: 'hidden'}}>
                      <h4 style={{
                        margin: '0 0 4px 0', 
                        fontSize: '13px',
                        lineHeight: '1.3',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word'
                      }}>{item.name}</h4>
                      {item.variation_name && (
                        <p style={{margin: '0 0 4px 0', fontSize: '10px', color: '#666'}}>
                          {item.variation_name}
                        </p>
                      )}
                      <p style={{margin: '0', fontSize: '14px', fontWeight: 'bold', color: '#c9a961'}}>
                        R{item.price}
                      </p>
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', flexShrink: 0}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                        <button
                          onClick={() => handleUpdateQuantity(index, item.quantity - 1)}
                          style={{
                            width: '24px',
                            height: '24px',
                            border: '1px solid #ddd',
                            background: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          -
                        </button>
                        <span style={{fontSize: '13px', fontWeight: 'bold', minWidth: '20px', textAlign: 'center'}}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleUpdateQuantity(index, item.quantity + 1)}
                          style={{
                            width: '24px',
                            height: '24px',
                            border: '1px solid #ddd',
                            background: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemoveFromCart(index)}
                        style={{
                          padding: '4px 10px',
                          background: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <i className="fas fa-trash"></i> Remove
                      </button>
                    </div>
                  </div>
                ))}

                {/* Cart Total */}
                <div style={{
                  background: '#f9f9f9',
                  padding: '15px',
                  borderRadius: '8px',
                  marginTop: '15px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '12px'
                  }}>
                    <span>Total:</span>
                    <span style={{color: '#c9a961'}}>R{getCartTotal()}</span>
                  </div>
                  <button
                    onClick={handleCheckout}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#c9a961',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-check-circle"></i> Proceed to Checkout
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Past Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            {pastOrders.length === 0 ? (
              <div style={{textAlign: 'center', padding: '50px', color: '#999'}}>
                <i className="fas fa-inbox" style={{fontSize: '64px', marginBottom: '20px'}}></i>
                <p>No orders yet. Start shopping at Prive Bakery!</p>
                <button 
                  onClick={() => setActiveTab('products')}
                  className="blog-read-more"
                  style={{marginTop: '20px'}}
                >
                  Browse Products
                </button>
              </div>
            ) : (
              <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                {pastOrders.map((order) => (
                  <div key={order.id} style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    border: '1px solid #eee'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '10px',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <div>
                        <h3 style={{margin: 0, fontSize: '14px', fontWeight: 'bold'}}>
                          Order #{order.number}
                        </h3>
                        <p style={{margin: '3px 0 0 0', color: '#666', fontSize: '11px'}}>
                          <i className="far fa-calendar"></i> {new Date(order.date_created).toLocaleDateString()}
                        </p>
                      </div>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        background: getOrderStatusColor(order.status),
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        textTransform: 'capitalize'
                      }}>
                        {order.status}
                      </span>
                    </div>

                    {/* Order Items Summary */}
                    <div style={{marginBottom: '10px'}}>
                      {order.line_items.slice(0, 2).map((item) => (
                        <div key={item.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '6px 0',
                          fontSize: '12px',
                          borderBottom: '1px solid #f0f0f0'
                        }}>
                          <div style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            paddingRight: '10px'
                          }}>
                            <strong>{item.name}</strong>
                            <span style={{color: '#666', marginLeft: '6px'}}>x{item.quantity}</span>
                          </div>
                          <div style={{fontSize: '12px', fontWeight: 'bold', color: '#c9a961', flexShrink: 0}}>
                            R{item.total}
                          </div>
                        </div>
                      ))}
                      {order.line_items.length > 2 && (
                        <p style={{fontSize: '11px', color: '#666', margin: '6px 0 0 0'}}>
                          +{order.line_items.length - 2} more item(s)
                        </p>
                      )}
                    </div>

                    {/* Order Total */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: '10px',
                      borderTop: '2px solid #eee',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      marginBottom: '10px'
                    }}>
                      <span>Total:</span>
                      <span style={{color: '#c9a961'}}>R{order.total}</span>
                    </div>

                    {/* View Order Details Button */}
                    <button 
                      onClick={() => setSelectedOrder(order)}
                      style={{
                        padding: '8px 16px',
                        background: '#c9a961',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        width: '100%',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      <i className="fas fa-eye"></i> Full Order Details
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Order Details Modal */}
        {selectedOrder && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }} onClick={() => setSelectedOrder(null)}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              position: 'relative'
            }} onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div style={{
                position: 'sticky',
                top: 0,
                background: 'white',
                padding: '16px',
                borderBottom: '2px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: '12px 12px 0 0'
              }}>
                <h2 style={{margin: 0, fontSize: '18px'}}>Order #{selectedOrder.number}</h2>
                <button 
                  onClick={() => setSelectedOrder(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0',
                    width: '30px',
                    height: '30px'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Modal Content */}
              <div style={{padding: '16px'}}>
                {/* Order Info */}
                <div style={{marginBottom: '16px'}}>
                  <p style={{margin: '0 0 8px 0', fontSize: '12px', color: '#666'}}>
                    <i className="far fa-calendar"></i> {new Date(selectedOrder.date_created).toLocaleString()}
                  </p>
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '12px',
                    background: getOrderStatusColor(selectedOrder.status),
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    textTransform: 'capitalize'
                  }}>
                    {selectedOrder.status}
                  </span>
                </div>

                {/* All Order Items */}
                <div style={{
                  background: '#f9f9f9',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <h3 style={{margin: '0 0 12px 0', fontSize: '14px'}}>Order Items</h3>
                  {selectedOrder.line_items.map((item, index) => (
                    <div key={item.id} style={{
                      padding: '10px 0',
                      borderBottom: index < selectedOrder.line_items.length - 1 ? '1px solid #ddd' : 'none'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px'
                      }}>
                        <strong style={{fontSize: '13px', flex: 1}}>{item.name}</strong>
                        <span style={{fontSize: '13px', fontWeight: 'bold', color: '#c9a961'}}>
                          R{item.total}
                        </span>
                      </div>
                      <p style={{margin: 0, fontSize: '11px', color: '#666'}}>
                        Quantity: {item.quantity} × R{item.price}
                      </p>
                      {item.meta_data && item.meta_data.length > 0 && (
                        <div style={{marginTop: '4px', fontSize: '11px', color: '#666'}}>
                          {item.meta_data.map((meta, idx) => (
                            <div key={idx}>{meta.display_key}: {meta.display_value}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Shipping Address */}
                {selectedOrder.shipping && (
                  <div style={{
                    background: '#f9f9f9',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <h3 style={{margin: '0 0 8px 0', fontSize: '14px'}}>
                      <i className="fas fa-shipping-fast"></i> Shipping Address
                    </h3>
                    <p style={{margin: 0, fontSize: '12px', lineHeight: '1.6'}}>
                      {selectedOrder.shipping.first_name} {selectedOrder.shipping.last_name}<br/>
                      {selectedOrder.shipping.address_1}<br/>
                      {selectedOrder.shipping.address_2 && <>{selectedOrder.shipping.address_2}<br/></>}
                      {selectedOrder.shipping.city}, {selectedOrder.shipping.state} {selectedOrder.shipping.postcode}<br/>
                      {selectedOrder.shipping.country}
                    </p>
                  </div>
                )}

                {/* Payment & Totals */}
                <div style={{
                  background: '#f9f9f9',
                  padding: '12px',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    fontSize: '12px'
                  }}>
                    <span>Subtotal:</span>
                    <span>R{selectedOrder.total}</span>
                  </div>
                  {selectedOrder.shipping_total && parseFloat(selectedOrder.shipping_total) > 0 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                      fontSize: '12px'
                    }}>
                      <span>Shipping:</span>
                      <span>R{selectedOrder.shipping_total}</span>
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: '8px',
                    borderTop: '2px solid #ddd',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#c9a961'
                  }}>
                    <span>Total:</span>
                    <span>R{selectedOrder.total}</span>
                  </div>
                  <p style={{margin: '8px 0 0 0', fontSize: '11px', color: '#666'}}>
                    Payment: {selectedOrder.payment_method_title}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <BottomNav />
    </div>
  );
};

export default PriveShop;
